/**
 * EditReminderModal Component
 * Simple modal for editing reminder text only.
 */

import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"
import { useGrowthReminders } from "../hooks/useGrowthReminders"

const MAX_TEXT_LENGTH = 80

interface EditReminderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reminderId: string | undefined
  defaultText: string | undefined
}

export function EditReminderModal({
  open,
  onOpenChange,
  reminderId,
  defaultText,
}: EditReminderModalProps) {
  const { t } = useTranslation("myRituality")
  const { updateReminder, isUpdating } = useGrowthReminders()

  const [text, setText] = useState("")

  useEffect(() => {
    if (open) {
      setText(defaultText || "")
    }
  }, [open, defaultText])

  const handleUpdate = () => {
    if (!reminderId || !text.trim()) return

    updateReminder(
      { reminderId, data: { text: text.trim() } },
      {
        onSuccess: () => {
          toast.success(t("reminders.modal.saved"))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t("reminders.modal.saveFailed"))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-4" onInteractOutside={e => e.preventDefault()}>
        <DialogHeader className="pt-5 px-3.5">
          <DialogTitle className="text-[24px] font-semibold uppercase">Edit Reminder</DialogTitle>
          <DialogDescription className="text-[14px] text-muted-foreground leading-[25px]">
            Update your growth reminder text
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Text Input Section */}
          <div className="space-y-2 px-3">
            <label className="text-[12px] font-medium uppercase leading-[12px]">
              {t("reminders.modal.inputLabel")} *
            </label>
            <input
              type="text"
              value={text}
              onChange={e => setText(e.target.value.slice(0, MAX_TEXT_LENGTH))}
              onKeyDown={e => {
                if (e.key === "Enter" && text.trim().length >= 1) {
                  e.preventDefault()
                  handleUpdate()
                }
              }}
              placeholder={t("reminders.modal.inputPlaceholder")}
              disabled={isUpdating}
              className="w-full min-h-[35px] rounded-none border-b border-border bg-transparent py-1 text-[12px] text-primary placeholder:text-muted-foreground focus:outline-none resize-none leading-[20px]"
            />
            <p className="text-xs text-muted-foreground">
              {text.length}/{MAX_TEXT_LENGTH}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 px-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isUpdating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isUpdating || !text.trim()} className="flex-1">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
